import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/use-auth';
import { UserProfile } from '../../contexts/auth-context';
import { cn } from '@/lib/utils';
import {
  LogOut,
  ChevronDown,
  Bell,
  Search,
  Menu,
  X,
  Sun,
  Moon,
} from 'lucide-react';
import { useColorMode } from '../../contexts/ColorModeContext';
import { useAIPageContext } from '../../hooks/use-ai-page-context';
import { useAIHealthCompanionStore } from '../../stores/ai-health-companion-store';
import { useHealthNudges } from '../../hooks/use-health-nudges';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { navigationGroups } from '../../constants/navigation-groups';
import { SidebarGroup } from '../navigation/SidebarGroup';
import { QuickActionsBar } from '../navigation/QuickActionsBar';
import Breadcrumbs from '../navigation/breadcrumbs';
import IconRail from '../navigation/IconRail';
import BottomTabBar from '../navigation/BottomTabBar';
import SubNavBar from '../navigation/SubNavBar';
import CompanionPanel from '../companion/CompanionPanel';
import SkipToMain from './SkipToMain';

interface AppShellProps {
  children: React.ReactNode;
}

const logoHeartbeatAnimation = `
@keyframes heartbeat {
  0% { transform: scale(1); }
  5% { transform: scale(1.1); }
  10% { transform: scale(1); }
  15% { transform: scale(1.1); }
  20% { transform: scale(1); }
  100% { transform: scale(1); }
}
`;

interface HeaderProps {
  user: UserProfile | null;
  signOut: () => void;
  getInitials: (user: UserProfile | null) => string;
  onMenuToggle: () => void;
  isSidebarOpen: boolean;
}

const Header: React.FC<HeaderProps> = ({ user, signOut, getInitials, onMenuToggle, isSidebarOpen }) => {
  const navigate = useNavigate();
  const { colorMode, toggleColorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  return (
    <nav className="flex-shrink-0 bg-card/80 backdrop-blur-md border-b border-border z-30 relative">
      <div className="px-4 mx-auto w-full">
        <div className="flex justify-between items-center h-14">
          {/* Left: Menu toggle + Logo */}
          <div className="flex items-center gap-2">
            <button
              onClick={onMenuToggle}
              className={cn(
                'hidden lg:flex items-center justify-center p-2 rounded-lg',
                'text-muted-foreground',
                'hover:bg-kaimed-sand/20 dark:hover:bg-kaimed-stone/20',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-kaimed-sage',
                'transition-colors duration-200',
              )}
              aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
              aria-expanded={isSidebarOpen}
            >
              {isSidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

            <Link to="/" className="flex items-center space-x-2">
              <style>{logoHeartbeatAnimation}</style>
              <img
                className="h-10 w-auto"
                src="/logo-new.png"
                alt="KaiMed Logo"
                style={{
                  animation: 'heartbeat 2s ease-in-out infinite',
                  transformOrigin: 'center',
                }}
              />
            </Link>
          </div>

          {/* Center: Search (desktop only) */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search health records, medications..."
                className={cn(
                  'w-full pl-10 pr-4 py-2 text-sm rounded-full',
                  'bg-muted/50 dark:bg-muted/30',
                  'border border-border',
                  'text-foreground',
                  'placeholder-muted-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-kaimed-sage focus:border-transparent',
                  'transition-all duration-200',
                )}
                aria-label="Search"
              />
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center space-x-2">
            {/* Theme toggle button */}
            <button 
              onClick={toggleColorMode} 
              className="group hidden sm:flex px-3 py-1.5 rounded-full items-center justify-center gap-2 text-sm font-medium border border-[var(--border-subtle)] bg-[var(--background-surface)] text-[var(--text-secondary)] hover:text-[var(--brand-primary)] hover:border-[var(--brand-primary-subtle)] hover:bg-[var(--brand-primary-subtle)] shadow-sm transition-all duration-300"
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? (
                <>
                  <Sun size={16} className="transition-transform group-hover:rotate-45" />
                  <span>Light</span>
                </>
              ) : (
                <>
                  <Moon size={16} className="transition-transform group-hover:-rotate-12" />
                  <span>Dark</span>
                </>
              )}
            </button>

            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'relative p-2 rounded-full',
                    'text-muted-foreground',
                    'hover:bg-kaimed-sand/20 dark:hover:bg-kaimed-stone/20',
                    'focus:outline-none focus:ring-2 focus:ring-kaimed-sage',
                    'transition-colors duration-200',
                  )}
                  aria-label="View notifications"
                >
                  <Bell className="h-5 w-5" />
                  <span className="absolute top-1 right-1 flex h-2 w-2 rounded-full bg-kaimed-clay" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 p-4 text-center">
                <Bell className="mx-auto h-8 w-8 text-muted-foreground mb-2 opacity-50" />
                <p className="text-sm font-medium">No new notifications</p>
                <p className="text-xs text-muted-foreground mt-1">You're all caught up!</p>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Profile */}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center space-x-2 p-1 rounded-full hover:bg-kaimed-sand/20 dark:hover:bg-kaimed-stone/20 focus:outline-none focus:ring-2 focus:ring-kaimed-sage transition-colors"
                    aria-label="User menu"
                  >
                    <Avatar className="h-8 w-8 ring-2 ring-kaimed-sage/20">
                      <AvatarImage src={user.avatarUrl} alt={user.firstName || 'User'} />
                      <AvatarFallback className="text-xs bg-kaimed-sage/10 text-kaimed-sage-dark">{getInitials(user)}</AvatarFallback>
                    </Avatar>
                    <ChevronDown className="h-3 w-3 text-muted-foreground hidden sm:block" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>
                    {user.firstName} {user.lastName}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>Profile</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')}>Settings</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut} disabled={!signOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

const GroupedSidebar: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'flex flex-col bg-card/95 backdrop-blur-sm border-r border-border z-40',
          'transition-all duration-300 ease-in-out',
          // Mobile: fixed overlay sidebar
          'fixed top-14 bottom-0 left-0 lg:relative lg:top-auto lg:bottom-auto',
          isOpen ? 'w-60 translate-x-0' : 'w-0 -translate-x-full lg:translate-x-0 lg:w-0',
          'overflow-hidden',
        )}
        aria-label="Grouped navigation"
      >
        <nav className="flex-1 overflow-y-auto px-2 pt-4 pb-2 min-w-[240px]">
          {navigationGroups.map((group) => (
            <SidebarGroup
              key={group.id}
              group={group}
              onItemClick={() => {
                // Close sidebar on mobile after navigation
                if (window.innerWidth < 1024) onClose();
              }}
            />
          ))}
        </nav>

        <QuickActionsBar />
      </aside>
    </>
  );
};

const AppContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <main
      id="main-content"
      className="flex-1 w-full overflow-y-auto bg-background"
      tabIndex={-1}
    >
      <Breadcrumbs className="border-b border-border/50" />
      <SubNavBar />
      <div className="min-h-screen pb-20 lg:pb-8">{children}</div>
    </main>
  );
};

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const { isPanelOpen, togglePanel } = useAIHealthCompanionStore((s) => ({
    isPanelOpen: s.isPanelOpen,
    togglePanel: s.togglePanel,
  }));
  const { unreadCount: nudgeBadgeCount } = useHealthNudges();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useAIPageContext();

  const handleCompanionToggle = useCallback(() => {
    togglePanel();
  }, [togglePanel]);

  const handleCompanionClose = useCallback(() => {
    if (isPanelOpen) togglePanel();
  }, [isPanelOpen, togglePanel]);

  const handleSidebarToggle = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const handleSidebarClose = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const getInitials = (userArg: UserProfile | null): string => {
    if (!userArg || (!userArg.firstName && !userArg.lastName)) return '?';
    return `${userArg.firstName?.[0] || ''}${userArg.lastName?.[0] || ''}`;
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      <SkipToMain />
      <Header
        user={user}
        signOut={signOut}
        getInitials={getInitials}
        onMenuToggle={handleSidebarToggle}
        isSidebarOpen={isSidebarOpen}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Icon Rail */}
        <IconRail
          companionOpen={isPanelOpen}
          onCompanionToggle={handleCompanionToggle}
          nudgeBadgeCount={nudgeBadgeCount}
        />

        {/* Grouped Sidebar (toggleable) */}
        <GroupedSidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />

        {/* Main Content */}
        <AppContent>{children}</AppContent>

        {/* AI Companion Panel */}
        <CompanionPanel isOpen={isPanelOpen} onClose={handleCompanionClose} />
      </div>

      {/* Mobile Bottom Tab Bar */}
      <BottomTabBar
        companionOpen={isPanelOpen}
        onCompanionToggle={handleCompanionToggle}
        nudgeBadgeCount={nudgeBadgeCount}
      />
    </div>
  );
};

export default AppShell;
